import React, { useState, useEffect } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  LayoutDashboard, Building2,
  FolderKanban, ShieldCheck, LogOut, ChevronLeft, ChevronRight,
  Eye, Map, Activity, ChevronDown, ShieldAlert,
  Layers, MapPin, CalendarDays, Siren, Shield, UserCog, Users, Settings, User,
  Menu, X, Smartphone,
} from 'lucide-react';

import { HelpCircle, Archive, ClipboardList, BookOpen } from 'lucide-react';

// ─── Nav structure per role ───────────────────────────────────────────────────
// Each group: { label: string|null, items: [...] }
const navGroupsByRole = {
  super_admin: [
    {
      label: null,
      items: [
        { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard, exact: true, menuId: 'menu-dashboard' },
      ],
    },
    {
      label: 'OPERACIONES',
      items: [
        { to: '/dashboard/clientes',     label: 'Clientes',     icon: Building2,    menuId: 'menu-clientes'  },
        { to: '/dashboard/proyectos',    label: 'Proyectos',    icon: FolderKanban, menuId: 'menu-proyectos' },
        { to: '/dashboard/events',       label: 'Eventos',      icon: CalendarDays, menuId: 'menu-eventos'   },
        // #EvalsGlobalList — vista global agregada de TODAS las evaluaciones.
        { to: '/dashboard/evaluaciones', label: 'Evaluaciones', icon: ClipboardList, menuId: 'menu-evaluaciones' },
      ],
    },
    {
      label: 'DIRECTORIO',
      items: [
        { to: '/dashboard/staff',             label: 'Personal',    icon: Users,     menuId: 'menu-personal'    },
        { to: '/dashboard/locations',         label: 'Locaciones',  icon: MapPin,    menuId: 'menu-locaciones'  },
        { to: '/dashboard/emergency-services',label: 'Emergencias', icon: Siren,     menuId: 'menu-emergencias' },
        { to: '/dashboard/providers', label: 'Proveedores', icon: Siren, menuId: 'menu-proveedores' },
        { to: '/dashboard/authorities',       label: 'Autoridades', icon: Shield,    menuId: 'menu-autoridades' },
      ],
    },
    {
      label: 'ADMINISTRACIÓN',
      items: [
        { to: '/dashboard/usuarios',          label: 'Usuarios',      icon: UserCog,     menuId: 'menu-usuarios'      },
        { to: '/dashboard/templates',         label: 'Constructor',   icon: Layers,      menuId: 'menu-constructor'   },
        { to: '/dashboard/auditoria',         label: 'Auditoría',     icon: Activity,    menuId: 'menu-auditoria'     },
        { to: '/dashboard/wiki',              label: 'Wiki SOEM',     icon: BookOpen,    menuId: 'menu-wiki'          },
        { to: '/dashboard/faq',               label: 'FAQ / Ayuda',   icon: HelpCircle,  menuId: 'menu-faq'           },
        { to: '/dashboard/archivo',           label: 'Archivo',       icon: Archive,     menuId: 'menu-archivo'       },
        { to: '/dashboard/modo-espejo',       label: 'Modo Espejo',   icon: Eye,         menuId: 'menu-modo-espejo'   },
        { to: '/dashboard/configuracion',     label: 'Configuracion', icon: Settings,    menuId: 'menu-configuracion' },
      ],
    },
  ],

  lider_proyecto: [
    {
      label: null,
      items: [
        { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard, exact: true, menuId: 'menu-dashboard-lider' },
        { to: '/dashboard/templates', label: 'Constructor', icon: Layers, menuId: 'menu-constructor' },
      ],
    },
    {
      label: 'OPERACIONES',
      items: [
        { to: '/dashboard/clientes',     label: 'Clientes',     icon: Building2,     menuId: 'menu-clientes-lider' },
        { to: '/dashboard/proyectos',    label: 'Proyectos',    icon: FolderKanban,  menuId: 'menu-proyectos' },
        { to: '/dashboard/events',       label: 'Eventos',      icon: CalendarDays,  menuId: 'menu-eventos'   },
        // #EvalsGlobalList — los líderes también pueden ver el listado global
        // (filtrado por ownership en el backend).
        { to: '/dashboard/evaluaciones', label: 'Evaluaciones', icon: ClipboardList, menuId: 'menu-evaluaciones-lider' },
      ],
    },
    {
      label: 'DIRECTORIO',
      items: [
        { to: '/dashboard/staff',            label: 'Personal',    icon: Users,     menuId: 'menu-personal'    },
        { to: '/dashboard/locations',        label: 'Locaciones',  icon: MapPin,    menuId: 'menu-locaciones'  },
        { to: '/dashboard/emergency-services',label: 'Emergencias', icon: Siren,    menuId: 'menu-emergencias' },
        { to: '/dashboard/providers', label: 'Proveedores', icon: Siren, menuId: 'menu-proveedores' },
        { to: '/dashboard/authorities',      label: 'Autoridades', icon: Shield,    menuId: 'menu-autoridades' },
      ],
    },
    {
      label: null,
      items: [
        { to: '/dashboard/wiki',  label: 'Wiki SOEM', icon: BookOpen,   menuId: 'menu-wiki-lider'  },
        { to: '/dashboard/ayuda', label: 'Ayuda',     icon: HelpCircle, menuId: 'menu-ayuda-lider' },
      ],
    },
  ],

  levantador: [
    {
      label: null,
      items: [
        { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard, exact: true },
      ],
    },
    {
      label: 'OPERACIONES',
      items: [
        { to: '/dashboard/proyectos', label: 'Mis Proyectos', icon: FolderKanban, menuId: 'menu-proyectos-lev' },
        // Levantador en campo necesita ver eventos asignados (#81Co4ZSy).
        { to: '/dashboard/events', label: 'Eventos', icon: CalendarDays, menuId: 'menu-eventos' },
      ],
    },
    {
      label: 'DIRECTORIO',
      items: [
        { to: '/dashboard/locations', label: 'Locaciones', icon: MapPin, menuId: 'menu-locaciones-lev' },
        // Emergencias/autoridades visibles para alta y consulta (#NrvzkvT2, #tUR0xNXc).
        { to: '/dashboard/emergency-services', label: 'Emergencias', icon: Siren, menuId: 'menu-emergencias' },
        { to: '/dashboard/providers', label: 'Proveedores', icon: Siren, menuId: 'menu-proveedores' },
        { to: '/dashboard/authorities', label: 'Autoridades', icon: Shield, menuId: 'menu-autoridades' },
      ],
    },
    {
      label: null,
      items: [
        { to: '/dashboard/wiki',  label: 'Wiki SOEM', icon: BookOpen,   menuId: 'menu-wiki-lev'  },
        { to: '/dashboard/ayuda', label: 'Ayuda',     icon: HelpCircle, menuId: 'menu-ayuda-lev' },
      ],
    },
  ],

  cliente: [
    {
      label: null,
      items: [
        { to: '/cliente', label: 'Dashboard', icon: LayoutDashboard, exact: true },
      ],
    },
    {
      label: 'MI EMPRESA',
      items: [
        { to: '/cliente/eventos', label: 'Eventos', icon: CalendarDays, menuId: 'menu-eventos-cli' },
        { to: '/cliente/incidentes', label: 'Incidentes',       icon: ShieldAlert, menuId: 'menu-incidentes-cli' },
        { to: '/cliente/mapa',       label: 'Mapa Interactivo', icon: Map,         menuId: 'menu-mapa-cli' },
      ],
    },
    {
      label: null,
      items: [
        { to: '/cliente/wiki',  label: 'Wiki SOEM', icon: BookOpen,   menuId: 'menu-wiki-cli'  },
        { to: '/cliente/ayuda', label: 'Ayuda',     icon: HelpCircle, menuId: 'menu-ayuda-cli' },
      ],
    },
  ],
};

// ─── Single nav link ──────────────────────────────────────────────────────────
// #TourAnchors — SidebarContent se renderiza DOS veces (drawer móvil +
// sidebar de escritorio), así que sin prefijo cada menuId quedaba
// duplicado en el DOM. driver.js resuelve sus anclas con
// document.querySelector, que devuelve el PRIMERO — el del drawer móvil,
// que en escritorio está oculto y desplazado fuera de pantalla
// (-translate-x-full). Resultado: el tooltip del tour se dibujaba
// pegado arriba a la izquierda apuntando a la nada, en vez de sobre la
// entrada del menú. El sidebar de escritorio conserva el id canónico
// (el que buscan los pasos del tour) y el drawer móvil lleva prefijo.
function SidebarLink({ to, label, icon: Icon, exact, collapsed, menuId, idPrefix = '', onClick }) {
  return (
    <NavLink
      to={to}
      end={exact}
      id={menuId ? `${idPrefix}${menuId}` : undefined}
      className={({ isActive }) => isActive ? 'sidebar-link-active' : 'sidebar-link'}
      data-testid={`nav-${label.toLowerCase().replace(/\s+/g, '-')}`}
      onClick={onClick}
    >
      <Icon size={18} strokeWidth={1.5} />
      {!collapsed && <span>{label}</span>}
    </NavLink>
  );
}

// ─── Group separator label ────────────────────────────────────────────────────
function SectionLabel({ label, collapsed }) {
  if (collapsed) {
    return <div className="my-2 border-t border-slate-800" />;
  }
  return (
    <div className="pt-4 pb-1 px-3">
      <div className="border-t border-slate-800 mb-2" />
      <span style={{ fontSize: '10px', color: '#6B7280', fontWeight: 600, letterSpacing: '0.08em' }}>
        {label}
      </span>
    </div>
  );
}

export default function Layout({ isCliente }) {
  const { activeUser, activeRole, mirrorUser, setMirrorUser, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const role = isCliente ? 'cliente' : activeRole;
  const navGroups = navGroupsByRole[role] || navGroupsByRole.levantador;

  const roleLabel = {
    super_admin: 'Super Admin',
    lider_proyecto: 'Líder de Proyecto',
    levantador: 'Levantador',
    cliente: 'Cliente',
  };

  // Close mobile menu on route change
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  // #TourMobileAnchors — en pantalla chica el sidebar de escritorio está
  // en display:none, así que el tour tiene que anclarse al drawer; y
  // cerrado vive fuera de pantalla. El tour pide abrirlo por evento para
  // no obligar a levantar este estado a un contexto global. El efecto de
  // arriba lo vuelve a cerrar en cada navegación, por eso el tour
  // reemite el evento después de cada cambio de ruta.
  useEffect(() => {
    const onTourMenu = (e) => setMobileOpen(!!e.detail?.open);
    window.addEventListener('soem:tour-menu', onTourMenu);
    return () => window.removeEventListener('soem:tour-menu', onTourMenu);
  }, []);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileOpen]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const closeMobileMenu = () => setMobileOpen(false);

  // Sidebar content (shared between desktop and mobile).
  // Se llama como función, no como <SidebarContent />: definida dentro de Layout, cada render
  // creaba un tipo de componente nuevo y React remontaba todo el menú al cambiar de página. El
  // tour guiado se quedaba con nodos desconectados (globo arriba a la izquierda sin señalar nada)
  // y el scroll del menú volvía arriba en cada navegación.
  const renderSidebarContent = ({ isMobile = false }) => (
    <>
      {/* Logo */}
      <div className="flex items-center gap-3 px-4 py-5 border-b border-white/10">
        <div className="w-8 h-8 bg-soem-primary rounded-sm flex items-center justify-center flex-shrink-0">
          <ShieldCheck size={18} className="text-white" strokeWidth={2} />
        </div>
        {(isMobile || !collapsed) && (
          <div>
            <div className="text-white font-black text-lg leading-none" style={{ fontFamily: 'Chivo, sans-serif' }}>SOEM</div>
            <div className="text-slate-400 text-xs">Plataforma de Seguridad</div>
          </div>
        )}
        {/* Close button for mobile */}
        {isMobile && (
          <button
aria-label="Cerrar"             onClick={closeMobileMenu}
            className="ml-auto p-2 text-slate-400 hover:text-white transition-colors"
            data-testid="close-mobile-menu-btn"
          >
            <X size={20} />
          </button>
        )}
      </div>

      {/* Navigation — grouped with section labels */}
      <nav className="flex-1 px-2 py-3 overflow-y-auto">
        {navGroups.map((group, gi) => (
          <div key={gi}>
            {/* Section separator + label (skip for first group with no label) */}
            {group.label && (
              <SectionLabel label={group.label} collapsed={isMobile ? false : collapsed} />
            )}
            <div className={`space-y-0.5 ${gi === 0 ? 'mt-1' : ''}`}>
              {group.items.map(item => (
                <SidebarLink
                  key={item.to}
                  {...item}
                  collapsed={isMobile ? false : collapsed}
                  // #TourAnchors — solo el sidebar de escritorio lleva el
                  // id canónico; el drawer móvil va prefijado para no
                  // duplicarlo y robarle el ancla al tour.
                  idPrefix={isMobile ? 'mobile-' : ''}
                  onClick={isMobile ? closeMobileMenu : undefined}
                />
              ))}
            </div>
          </div>
        ))}
      </nav>

      {/* User info */}
      <div className="border-t border-white/10 p-3">
        {(isMobile || !collapsed) && (
          <div className="px-2 mb-2">
            <p className="text-white text-sm font-semibold truncate">{activeUser?.name}</p>
            <p className="text-slate-400 text-xs">{roleLabel[role]}</p>
          </div>
        )}
        <button
          onClick={handleLogout}
          className="flex items-center gap-3 w-full px-2 py-2 rounded-sm text-slate-400 hover:text-white hover:bg-red-600/20 transition-all text-sm"
          data-testid={isMobile ? "mobile-logout-btn" : "logout-btn"}
        >
          <LogOut size={16} strokeWidth={1.5} />
          {(isMobile || !collapsed) && <span>Cerrar sesión</span>}
        </button>
      </div>

      {/* Collapse toggle (desktop only) */}
      {!isMobile && (
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="flex items-center justify-center py-2 border-t border-white/10 text-slate-400 hover:text-white transition-colors"
          data-testid="sidebar-collapse-btn"
        >
          {collapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
        </button>
      )}
    </>
  );

  return (
    <div className="flex h-screen bg-soem-bg overflow-hidden">
      {/* Mirror Mode Banner */}
      {mirrorUser && (
        <div className="fixed top-0 left-0 right-0 z-50 bg-yellow-400 text-yellow-900 px-4 py-2 flex items-center justify-between text-sm font-semibold shadow-md">
          <div className="flex items-center gap-2">
            <Eye size={16} />
            <span className="hidden sm:inline">Estás viendo la plataforma como <strong>{mirrorUser.name}</strong> ({roleLabel[mirrorUser.role]})</span>
            <span className="sm:hidden">Modo Espejo: <strong>{mirrorUser.name}</strong></span>
          </div>
          <button
            onClick={() => {
              // #yabzzxVc — el Salir hacía setMirrorUser(null) + navigate
              // en el mismo tick. React batchea ambos, pero el RoleRoute
              // de la ruta actual (ej. /cliente) corría antes que se
              // commitara el cambio de activeRole, viendo cliente→super
              // admin en una ruta cliente-only → flash de access-denied.
              // Mismo patrón que activateMirror: persistir y recargar.
              setMirrorUser(null);
              window.location.replace('/dashboard');
            }}
            className="bg-yellow-600 text-white px-3 py-1 rounded-sm text-xs font-bold hover:bg-yellow-700"
            data-testid="exit-mirror-mode-btn"
          >
            Salir
          </button>
        </div>
      )}

      {/* Mobile Menu Overlay */}
      {mobileOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={closeMobileMenu}
          data-testid="mobile-menu-overlay"
        />
      )}

      {/* Mobile Sidebar (Drawer) */}
      <aside
        className={`
          fixed inset-y-0 left-0 z-50 w-72 bg-soem-navy flex flex-col transform transition-transform duration-300 ease-in-out lg:hidden
          ${mobileOpen ? 'translate-x-0' : '-translate-x-full'}
          ${mirrorUser ? 'pt-10' : ''}
        `}
        data-testid="mobile-sidebar"
      >
        {renderSidebarContent({ isMobile: true })}
      </aside>

      {/* Desktop Sidebar */}
      <aside
        id="sidebar-menu"
        className={`
          ${collapsed ? 'w-16' : 'w-64'} 
          bg-soem-navy flex-col transition-all duration-200 flex-shrink-0 
          ${mirrorUser ? 'mt-10' : ''}
          hidden lg:flex
        `}
        data-testid="sidebar"
      >
        {renderSidebarContent({ isMobile: false })}
      </aside>

      {/* Main content */}
      <div className={`flex-1 flex flex-col overflow-hidden ${mirrorUser ? 'mt-10' : ''}`}>
        {/* Header */}
        <header className="bg-white border-b border-slate-200 px-4 sm:px-6 py-3 flex items-center justify-between flex-shrink-0 shadow-sm">
          <div className="flex items-center gap-2 sm:gap-3">
            {/* Mobile Hamburger Button */}
            <button
              onClick={() => setMobileOpen(true)}
              className="p-2 -ml-2 text-slate-600 hover:text-soem-primary hover:bg-slate-100 rounded-sm transition-colors lg:hidden"
              data-testid="mobile-menu-btn"
            >
              <Menu size={22} />
            </button>
            <div className="flex items-center gap-2 text-slate-500 text-sm">
              <ShieldCheck size={16} className="text-soem-primary hidden sm:block" />
              <span className="text-soem-navy font-semibold">SOEM</span>
              <span className="text-slate-300 hidden sm:inline">/</span>
              <span className="hidden sm:inline">{roleLabel[role]}</span>
            </div>
          </div>
          <div className="flex items-center gap-2 sm:gap-3">
            {mirrorUser && (
              <span className="bg-yellow-100 text-yellow-800 text-xs px-2 py-1 rounded-full font-medium hidden sm:inline-block">
                Modo Espejo activo
              </span>
            )}
            <ProfileDropdown activeUser={activeUser} isCliente={isCliente} handleLogout={handleLogout} />
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-y-auto p-4 sm:p-6">
          <React.Suspense fallback={<div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-4 border-soem-primary border-t-transparent" /></div>}>
            <Outlet />
          </React.Suspense>
        </main>
      </div>
    </div>
  );
}

function ProfileDropdown({ activeUser, isCliente, handleLogout }) {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const profilePath = isCliente ? '/perfil' : '/perfil';

  return (
    <div className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-2 hover:bg-slate-50 px-2 py-1 rounded-sm transition-colors"
        data-testid="profile-menu-btn"
      >
        <div className="w-8 h-8 bg-soem-primary rounded-full flex items-center justify-center">
          <span className="text-white text-xs font-bold">{activeUser?.name?.charAt(0).toUpperCase()}</span>
        </div>
        <div className="hidden md:block text-left">
          <p className="text-sm font-semibold text-soem-navy">{activeUser?.name}</p>
          <p className="text-xs text-slate-500">{activeUser?.email}</p>
        </div>
        <ChevronDown size={14} className="text-slate-400" />
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-30" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-full mt-1 w-52 bg-white border border-slate-200 rounded-sm shadow-xl z-40 animate-fade-in">
            <div className="px-4 py-3 border-b border-slate-100">
              <p className="font-semibold text-soem-navy text-sm truncate">{activeUser?.name}</p>
              <p className="text-slate-400 text-xs truncate">{activeUser?.email}</p>
            </div>
            <button
              onClick={() => { setOpen(false); navigate(profilePath); }}
              className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-soem-navy hover:bg-blue-50 transition-colors"
              data-testid="profile-link-btn"
            >
              <User size={15} className="text-slate-400" /> Mi perfil
            </button>
            <a
              href="https://apps.apple.com/mx/app/soem-pg/id6762004834"
              target="_blank"
              rel="noopener noreferrer"
              className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-soem-navy hover:bg-blue-50 transition-colors"
              data-testid="ios-app-link-menu"
            >
              <Smartphone size={15} className="text-slate-400" /> App iOS
            </a>
            <div className="border-t border-slate-100">
              <button
                onClick={() => { setOpen(false); handleLogout(); }}
                className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors"
                data-testid="logout-dropdown-btn"
              >
                <LogOut size={15} /> Cerrar sesión
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
